import {
  getDesignExtensionImageResolution,
} from '@/types/design-extension';
import type {
  DesignExtensionGenerateRequest,
  DesignExtensionAspectRatio,
  DesignExtensionOutputKind,
  DesignExtensionPreset,
  DesignExtensionThreeViewFocus,
} from '@/types/design-extension';
import type {
  ImageTemplate,
  ImageTemplateListItem,
  InputSlot,
  InputSlotOption,
  TemplateVariant,
} from '@/types/image-template';

export type BuiltinDesignTemplateId =
  | '8f5ddf57-29d6-47df-9a91-bf7cbf1c8d41'
  | '1d3c0538-49cb-4c4d-a0f6-6d2b1f7b82e5';

type LegacyBuiltinDesignTemplateId =
  | 'builtin-design-extension-garment-variants'
  | 'builtin-design-extension-outfit-style-extensions';

type BuiltinDesignTemplateKnownId = BuiltinDesignTemplateId | LegacyBuiltinDesignTemplateId;

const BUILTIN_DESIGN_TEMPLATE_IDS = {
  garmentVariants: '8f5ddf57-29d6-47df-9a91-bf7cbf1c8d41',
  outfitStyleExtensions: '1d3c0538-49cb-4c4d-a0f6-6d2b1f7b82e5',
} as const satisfies Record<string, BuiltinDesignTemplateId>;

interface BuiltinDesignTemplateDefinition {
  id: BuiltinDesignTemplateId;
  legacyIds: readonly LegacyBuiltinDesignTemplateId[];
  preset: DesignExtensionPreset;
  coverUrl: string;
  name: string;
  nameZh: string;
  description: string;
  descriptionZh: string;
  inputSlots: InputSlot[];
}

const BUILTIN_TEMPLATE_TIMESTAMP = '2026-05-19T07:20:00.000Z';
const BUILTIN_DESIGN_TEMPLATE_KIND = 'design_extension';

const OUTPUT_KIND_OPTIONS: InputSlotOption[] = [
  { value: 'image', label: 'Product Image', label_zh: '实物图' },
  { value: 'sketch', label: 'Sketch Image', label_zh: '线稿图' },
];

const GARMENT_VARIANT_BOARD_COUNTS = [4, 8] as const;
const OUTFIT_STYLE_BOARD_COUNTS = [4, 8] as const;
const PERSON_MULTIVIEW_BOARD_COUNTS = [4] as const;

const DESIGN_EXTENSION_BOARD_ASPECT_RATIOS: Record<
  DesignExtensionPreset,
  Record<number, DesignExtensionAspectRatio>
> = {
  garment_variants: {
    4: '1:1',
    8: '16:9',
  },
  outfit_style_extensions: {
    4: '1:1',
    8: '16:9',
  },
  person_multiview: {
    4: '16:9',
  },
};

const BUILTIN_DESIGN_EXTENSION_DEFAULT_IMAGE_MODELS: Record<DesignExtensionPreset, string> = {
  garment_variants: 'gpt-image-2.5-flare-low',
  outfit_style_extensions: 'gpt-image-2.5-flare-low',
  person_multiview: 'gpt-image-2.5-flare-low',
};

const BUILTIN_DESIGN_TEMPLATE_DEFINITIONS: Record<BuiltinDesignTemplateId, BuiltinDesignTemplateDefinition> = {
  [BUILTIN_DESIGN_TEMPLATE_IDS.garmentVariants]: {
    id: BUILTIN_DESIGN_TEMPLATE_IDS.garmentVariants,
    legacyIds: ['builtin-design-extension-garment-variants'],
    preset: 'garment_variants',
    coverUrl: '/template-covers/一键白底图封面.png',
    name: 'Garment Variants',
    nameZh: '版型延伸',
    description: 'Extend one garment reference into multiple silhouette and pattern directions on a single board.',
    descriptionZh: '围绕同一件单品延伸多种版型与廓形方向，整理成一张设计延伸板。',
    inputSlots: [
      {
        key: 'reference_image',
        type: 'image',
        required: true,
        label: 'Reference image',
        label_zh: '参考图',
        hint: 'Upload one garment or look reference image.',
        hint_zh: '上传 1 张单品或穿搭参考图。',
      },
      {
        key: 'board_count',
        type: 'select',
        required: true,
        label: 'Board count',
        label_zh: '版面数量',
        default_value: '4',
        ui_style: 'pills',
        options: GARMENT_VARIANT_BOARD_COUNTS.map((value) => ({
          value: String(value),
          label: `${value} Cells`,
          label_zh: `${value} 格`,
        })),
      },
      {
        key: 'output_kind',
        type: 'select',
        required: true,
        label: 'Output',
        label_zh: '生成结果',
        default_value: 'image',
        ui_style: 'segment',
        options: OUTPUT_KIND_OPTIONS,
      },
      {
        key: 'creative_note',
        type: 'text',
        required: false,
        label: 'Creative note',
        label_zh: '补充说明',
        hint: 'Call out what should stay stable or which directions should be explored.',
        hint_zh: '补充希望保留或重点探索的方向。',
      },
    ],
  },
  [BUILTIN_DESIGN_TEMPLATE_IDS.outfitStyleExtensions]: {
    id: BUILTIN_DESIGN_TEMPLATE_IDS.outfitStyleExtensions,
    legacyIds: ['builtin-design-extension-outfit-style-extensions'],
    preset: 'outfit_style_extensions',
    coverUrl: '/template-covers/一键搭配封面.png',
    name: 'Outfit Styling Extensions',
    nameZh: '搭配风格延伸',
    description: 'Turn one styling reference into 4 or 8 white-background full-look directions while keeping garment detail and palette fidelity clear.',
    descriptionZh: '围绕 1 张搭配参考图延伸 4 套或 8 套白底 full-look 方向，保持服装细节、配色和搭配逻辑清晰可辨。',
    inputSlots: [
      {
        key: 'reference_image_1',
        type: 'image',
        required: true,
        label: 'Styling reference',
        label_zh: '参考图',
        hint: 'Upload one styling reference image.',
        hint_zh: '上传 1 张搭配参考图。',
      },
      {
        key: 'board_count',
        type: 'select',
        required: true,
        label: 'Board count',
        label_zh: '版面数量',
        default_value: '4',
        ui_style: 'pills',
        options: OUTFIT_STYLE_BOARD_COUNTS.map((value) => ({
          value: String(value),
          label: `${value} Cells`,
          label_zh: `${value} 格`,
        })),
      },
      {
        key: 'creative_note',
        type: 'text',
        required: false,
        label: 'Creative note',
        label_zh: '补充说明',
        hint: 'Describe the styling direction or the garment details and color relationships that must stay stable.',
        hint_zh: '补充希望保留的搭配方向，或必须稳定保留的服装细节与配色关系。',
      },
    ],
  },
};

function cloneInputSlot(slot: InputSlot): InputSlot {
  return {
    ...slot,
    options: slot.options?.map((option) => ({ ...option })),
  };
}

function cloneVariant(variant: TemplateVariant): TemplateVariant {
  return {
    ...variant,
    input_slots_override: variant.input_slots_override?.map(cloneInputSlot),
    model_params_override: variant.model_params_override ? { ...variant.model_params_override } : undefined,
  };
}

function cloneTemplate(template: ImageTemplate): ImageTemplate {
  return {
    ...template,
    model_params: { ...template.model_params },
    input_slots: template.input_slots.map(cloneInputSlot),
    demo_images: template.demo_images.map((demo) => ({ ...demo })),
    variants: template.variants.map(cloneVariant),
    tags: [...template.tags],
  };
}

function cloneTemplateListItem(template: ImageTemplateListItem): ImageTemplateListItem {
  return {
    ...template,
    input_slots: template.input_slots?.map(cloneInputSlot),
    demo_images: template.demo_images?.map((demo) => ({ ...demo })),
    variants: template.variants?.map(cloneVariant),
    tags: template.tags ? [...template.tags] : undefined,
  };
}

function createBuiltinTemplate(definition: BuiltinDesignTemplateDefinition): ImageTemplate {
  return {
    id: definition.id,
    name: definition.name,
    name_zh: definition.nameZh,
    description: definition.description,
    description_zh: definition.descriptionZh,
    cover_url: definition.coverUrl,
    output_type: 'image',
    capability: BUILTIN_DESIGN_TEMPLATE_KIND,
    prompt_template: '',
    model_params: {
      builtin_template_kind: BUILTIN_DESIGN_TEMPLATE_KIND,
      design_extension_preset: definition.preset,
    },
    input_slots: definition.inputSlots,
    demo_images: [
      {
        source_url: definition.coverUrl,
        result_url: definition.coverUrl,
        label: 'Preview',
      },
    ],
    variants: [],
    category: 'design',
    tags: ['design', 'design_extension', definition.preset],
    trending_score: 5,
    usage_count: 0,
    status: 'published',
    admin_only: false,
    published_at: BUILTIN_TEMPLATE_TIMESTAMP,
    created_by: null,
    created_at: BUILTIN_TEMPLATE_TIMESTAMP,
    updated_at: BUILTIN_TEMPLATE_TIMESTAMP,
    source: 'official',
    editable: false,
    reference_image_url: null,
  };
}

const BUILTIN_DESIGN_TEMPLATES_BY_ID: Record<BuiltinDesignTemplateId, ImageTemplate> = {
  [BUILTIN_DESIGN_TEMPLATE_IDS.garmentVariants]: createBuiltinTemplate(
    BUILTIN_DESIGN_TEMPLATE_DEFINITIONS[BUILTIN_DESIGN_TEMPLATE_IDS.garmentVariants],
  ),
  [BUILTIN_DESIGN_TEMPLATE_IDS.outfitStyleExtensions]: createBuiltinTemplate(
    BUILTIN_DESIGN_TEMPLATE_DEFINITIONS[BUILTIN_DESIGN_TEMPLATE_IDS.outfitStyleExtensions],
  ),
};

const BUILTIN_DESIGN_TEMPLATE_ID_ALIASES = Object.values(BUILTIN_DESIGN_TEMPLATE_DEFINITIONS).reduce<Record<string, BuiltinDesignTemplateId>>(
  (acc, definition) => {
    acc[definition.id] = definition.id;
    definition.legacyIds.forEach((legacyId) => {
      acc[legacyId] = definition.id;
    });
    return acc;
  },
  {},
);

const BUILTIN_DESIGN_TEMPLATE_LIST = Object.values(BUILTIN_DESIGN_TEMPLATES_BY_ID).map<ImageTemplateListItem>((template) => ({
  id: template.id,
  name: template.name,
  name_zh: template.name_zh,
  description: template.description,
  description_zh: template.description_zh,
  cover_url: template.cover_url,
  capability: template.capability,
  output_type: template.output_type,
  category: template.category,
  tags: [...template.tags],
  trending_score: template.trending_score,
  usage_count: template.usage_count,
  status: template.status,
  admin_only: template.admin_only,
  created_at: template.created_at,
  updated_at: template.updated_at,
  input_slots: template.input_slots.map(cloneInputSlot),
  demo_images: template.demo_images.map((demo) => ({ ...demo })),
  variants: template.variants.map(cloneVariant),
  source: template.source,
  editable: template.editable,
  reference_image_url: template.reference_image_url,
}));

function matchesSearch(template: Pick<ImageTemplateListItem, 'name' | 'name_zh' | 'description' | 'description_zh' | 'tags'>, search?: string | null): boolean {
  const normalized = search?.trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  const candidates = [
    template.name,
    template.name_zh,
    template.description,
    template.description_zh,
    ...(template.tags ?? []),
  ];
  return candidates.some((value) => typeof value === 'string' && value.toLowerCase().includes(normalized));
}

export function resolveBuiltinDesignTemplateId(id: string): BuiltinDesignTemplateId | null {
  return BUILTIN_DESIGN_TEMPLATE_ID_ALIASES[id] ?? null;
}

export function isBuiltinDesignTemplateId(id: string): id is BuiltinDesignTemplateKnownId {
  return resolveBuiltinDesignTemplateId(id) !== null;
}

export function getBuiltinDesignTemplateById(id: string): ImageTemplate | null {
  const resolvedId = resolveBuiltinDesignTemplateId(id);
  if (!resolvedId) {
    return null;
  }
  return cloneTemplate(BUILTIN_DESIGN_TEMPLATES_BY_ID[resolvedId]);
}

export function getBuiltinDesignLibraryTemplates(search?: string | null): ImageTemplateListItem[] {
  return BUILTIN_DESIGN_TEMPLATE_LIST
    .filter((template) => matchesSearch(template, search))
    .map(cloneTemplateListItem);
}

export function prependBuiltinDesignLibraryTemplates(
  templates: ImageTemplateListItem[],
  search?: string | null,
): ImageTemplateListItem[] {
  const builtinTemplates = getBuiltinDesignLibraryTemplates(search);
  if (builtinTemplates.length === 0) {
    return templates;
  }

  const existingIds = new Set(
    templates.map((template) => resolveBuiltinDesignTemplateId(template.id) ?? template.id),
  );
  const missingBuiltinTemplates = builtinTemplates.filter((template) => !existingIds.has(template.id));
  if (missingBuiltinTemplates.length === 0) {
    return templates;
  }

  return [...missingBuiltinTemplates, ...templates];
}

export function getBuiltinDesignExtensionPreset(
  templateOrId?: string | Pick<ImageTemplate, 'id' | 'model_params'> | null,
): DesignExtensionPreset | null {
  if (!templateOrId) {
    return null;
  }

  if (typeof templateOrId === 'string') {
    const resolvedId = resolveBuiltinDesignTemplateId(templateOrId);
    return resolvedId ? BUILTIN_DESIGN_TEMPLATE_DEFINITIONS[resolvedId].preset : null;
  }

  const resolvedId = resolveBuiltinDesignTemplateId(templateOrId.id);
  return resolvedId ? BUILTIN_DESIGN_TEMPLATE_DEFINITIONS[resolvedId].preset : null;
}

export function getBuiltinDesignExtensionBoardCountOptions(preset: DesignExtensionPreset): readonly number[] {
  if (preset === 'garment_variants') {
    return GARMENT_VARIANT_BOARD_COUNTS;
  }
  if (preset === 'outfit_style_extensions') {
    return OUTFIT_STYLE_BOARD_COUNTS;
  }
  return PERSON_MULTIVIEW_BOARD_COUNTS;
}

export function getBuiltinDesignExtensionAspectRatio(
  preset: DesignExtensionPreset,
  boardCount: number,
  preferredAspectRatio?: DesignExtensionAspectRatio | null,
): DesignExtensionAspectRatio {
  if (preset === 'person_multiview') {
    return DESIGN_EXTENSION_BOARD_ASPECT_RATIOS.person_multiview[boardCount] ?? '16:9';
  }

  const aspectRatio = DESIGN_EXTENSION_BOARD_ASPECT_RATIOS[preset][boardCount];
  if (aspectRatio) {
    return aspectRatio;
  }

  return preset === 'garment_variants' ? '1:1' : '16:9';
}

export function getBuiltinDesignExtensionDefaultOutputKind(
  preset: DesignExtensionPreset,
): DesignExtensionOutputKind {
  return preset === 'garment_variants' ? 'image' : 'image';
}

export function getBuiltinDesignExtensionDefaultImageModel(
  preset: DesignExtensionPreset,
): string {
  return BUILTIN_DESIGN_EXTENSION_DEFAULT_IMAGE_MODELS[preset];
}

export function buildBuiltinDesignExtensionRequest(params: {
  projectId: string;
  preset: DesignExtensionPreset;
  primarySourceImage: string;
  boardCount: number;
  outputKind?: string | null;
  creativeNote?: string | null;
  imageModel?: string | null;
  imageResolution?: DesignExtensionGenerateRequest['image_resolution'] | null;
  selectedCharacterModelId?: string | null;
  aspectRatio?: DesignExtensionAspectRatio | null;
  personMultiviewFocus?: DesignExtensionThreeViewFocus | null;
  threeViewFocus?: DesignExtensionThreeViewFocus | null;
  primarySourceClipId?: string | null;
  primarySourceLabel?: string | null;
  backgroundSourceImage?: string | null;
  backgroundSourceLabel?: string | null;
}): DesignExtensionGenerateRequest {
  const personMultiviewFocus = params.personMultiviewFocus ?? params.threeViewFocus ?? undefined;
  const boardCountOptions = getBuiltinDesignExtensionBoardCountOptions(params.preset);
  const normalizedBoardCount = params.preset === 'person_multiview' && personMultiviewFocus === 'portrait'
    ? 3
    : boardCountOptions.includes(params.boardCount)
    ? params.boardCount
    : boardCountOptions[0];
  const normalizedOutputKind = (
    params.outputKind === 'sketch' && params.preset === 'garment_variants'
      ? 'sketch'
      : getBuiltinDesignExtensionDefaultOutputKind(params.preset)
  );
  const creativeNote = params.creativeNote?.trim();
  const imageModel = params.imageModel?.trim() || getBuiltinDesignExtensionDefaultImageModel(params.preset);
  const imageResolution = getDesignExtensionImageResolution(imageModel, params.preset, personMultiviewFocus);
  const primarySourceLabel = params.primarySourceLabel?.trim() || 'Reference';
  const backgroundSourceImage = params.backgroundSourceImage?.trim();
  const backgroundSourceLabel = params.backgroundSourceLabel?.trim() || 'Background reference';

  if (params.preset === 'person_multiview' && !personMultiviewFocus) {
    throw new Error('person_multiview_focus is required for person_multiview');
  }

  const sources: DesignExtensionGenerateRequest['sources'] = [{
    role: 'primary',
    image_url: params.primarySourceImage,
    ...(params.primarySourceClipId ? { clip_id: params.primarySourceClipId } : {}),
    label: primarySourceLabel,
  }];

  if (backgroundSourceImage) {
    sources.push({
      role: 'background_reference',
      image_url: backgroundSourceImage,
      label: backgroundSourceLabel,
    });
  }

  return {
    project_id: params.projectId,
    lane: 'fashion',
    preset: params.preset,
    sources,
    board_count: normalizedBoardCount,
    aspect_ratio: getBuiltinDesignExtensionAspectRatio(params.preset, normalizedBoardCount, params.aspectRatio),
    output_kind: normalizedOutputKind,
    intensity: 'balanced',
    ...(creativeNote ? { creative_note: creativeNote } : {}),
    ...(personMultiviewFocus ? { person_multiview_focus: personMultiviewFocus } : {}),
    image_model: imageModel,
    image_resolution: imageResolution,
    ...(params.selectedCharacterModelId ? { selected_character_model_id: params.selectedCharacterModelId } : {}),
  };
}
